import { Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { ToggleRouterService } from '../toggle-router.service';

/**
 * @ignore
 * Asserts that an object is not null or undefined
 * @param object Object to assert
 *
 * @return boolean
 */
function isNullOrUndefined(object: any): boolean {
  return object == null;
}

/**
 * @ignore
 * Asserts that a value is not null or undefined
 *
 * @param val Value to assert
 * @param errorMessage Message to display in error scenario
 *
 * @throws error when value is null or undefined
 */
export function verifyRequiredAttribute(val: any | any[], errorMessage: string): Error | null {
  if ((Array.isArray(val) && val.some(isNullOrUndefined)) || isNullOrUndefined(val)) {
    throw new Error(errorMessage);
  }
  return;
}

/**
 * Structural directive that can be used inline to turn on or off a specific
 * component. The directive must receive as main input a boolean flag or a
 * feature ID.
 *
 * If a boolean flag is provided, the directive may use that to toggle the
 * feature on or off. If a string is provided, it will look into the toggle
 * router service for the id.
 *
 * By default, if nothing else is provided to the directive, it may behave as a
 * *ngIf. But, it can receive an ng-template, which will be treated as an
 * experimental component. In this case, the directive will switch between the
 * experimental feature and the current component.
 */
@Directive({
  selector: '[cbreTogglePoint]',
  standalone: true
})
export class TogglePointDirective {
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly experimentalRef = inject<TemplateRef<any>>(TemplateRef);
  private readonly toggleRouterService = inject(ToggleRouterService);

  readonly featureId = input<string | boolean>(undefined, { alias: 'cbreTogglePoint' });
  readonly cbreTogglePointReplaceWith = input<TemplateRef<any> | null>(null);

  constructor() {
    effect(() => {
      const fId = this.featureId();
      const replaceWithRef = this.cbreTogglePointReplaceWith();

      if (fId != null) {
        verifyRequiredAttribute(fId, 'featureId is a required attribute.');
      }

      const isEnabled = this.toggleRouterService.isFeatureEnabled(fId);
      this.viewContainer.clear();

      if (!!replaceWithRef) {
        this.viewContainer.createEmbeddedView(isEnabled ? replaceWithRef : this.experimentalRef);
      } else if (isEnabled) {
        this.viewContainer.createEmbeddedView(this.experimentalRef);
      }
    });
  }
}
